import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, HandCoins, CalendarClock, BellRing, Clock,
  MailOpen, BarChart3, Settings, LogOut, Sprout, ChevronDown,
  HeartHandshake, GraduationCap, BookOpen, ClipboardCheck,
  Award, UserSquare, Presentation, School, Inbox, MessageSquare, Send, Calendar, Sparkles, Ticket, NotebookPen,
  FolderOpen, LineChart, ClipboardList, ShoppingCart, Compass, LayoutGrid, UserCheck, FileSpreadsheet, AlarmClock, History, Wallet, Receipt, PieChart, Banknote, ChevronsDownUp, FileText, LifeBuoy, ShieldCheck,
} from "lucide-react";
import { useAuth, isStaff, isStudent, isTeacher, isAdminOrSegreteria, isFundraising, isEconomato } from "@/lib/auth";
import { canAccess, canAccessModule } from "@/lib/permissions";
import { NAV, AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import PushEnableButton from "@/components/PushEnableButton";
import { LanguageSwitch } from "@/lib/i18n";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useTicketCount } from "@/components/helpdesk/TicketAlert";

const fundraisingItems = [
  { to: "/", label: "Dashboard",       icon: LayoutDashboard, testId: NAV.dashboard, end: true, perm: null },
  { to: "/donatori",   label: "Donatori",       icon: Users,           testId: NAV.donors,          perm: "fundraising:donors" },
  { to: "/donazioni",  label: "Donazioni",      icon: HandCoins,       testId: NAV.donations,       perm: "fundraising:donations", end: true },
  { to: "/donazioni/cronologia", label: "Cronologia inserimenti", icon: History, testId: "nav-donations-audit", perm: "fundraising:audit" },
  { to: "/donazioni/riconciliazione", label: "Riconciliazione mensile", icon: FileSpreadsheet, testId: "nav-donations-recon", perm: "fundraising:reconciliation" },
  { to: "/scadenze",   label: "Scadenze",       icon: CalendarClock,   testId: NAV.schedules,       perm: "fundraising:schedules" },
  { to: "/followup",   label: "Follow-up",      icon: BellRing,        testId: NAV.followups,       perm: "fundraising:followup" },
  { to: "/donatori-ritardo", label: "Donatori in ritardo", icon: AlarmClock, testId: "nav-late-donors", perm: "fundraising:late" },
  { to: "/comunicazioni", label: "Comunicazioni", icon: MailOpen,      testId: NAV.communications,  perm: "fundraising:communications" },
  { to: "/report",     label: "Report",         icon: BarChart3,       testId: NAV.reports,         perm: "fundraising:reports" },
];

const modularItems = [
  { to: "/modulare",           label: "Dashboard",            icon: LayoutGrid,      testId: "nav-mod-dashboard", perm: "school:dashboard", end: true },
  { to: "/modulare/iscrizioni", label: "Iscrizioni",           icon: ClipboardList,   testId: "nav-mod-enrollments", perm: "school:students" },
  { to: "/modulare/studenti",  label: "Studenti",             icon: Users,           testId: "nav-mod-students",  perm: "school:students" },
  { to: "/modulare/corsi",     label: "Corsi",                icon: BookOpen,        testId: "nav-mod-courses",   perm: "school:courses" },
  { to: "/modulare/tutor",     label: "Tutor",                icon: UserCheck,       testId: "nav-mod-tutors",    perm: "school:students" },
  { to: "/modulare/colloqui",  label: "Colloqui",             icon: CalendarClock,   testId: "nav-mod-meetings",  perm: "school:students" },
  { to: "/modulare/pagamenti", label: "Pagamenti",            icon: Banknote,        testId: "nav-mod-payments",  perm: "school:students" },
  { to: "/modulare/docenti",   label: "Docenti",              icon: GraduationCap,   testId: "nav-mod-teachers",  perm: "school:teachers" },
  { to: "/modulare/attestati", label: "Attestati",            icon: Award,           testId: "nav-mod-certs",     perm: "school:certs" },
  { to: "/modulare/seminari",  label: "Seminari e Workshop",  icon: Presentation,    testId: "nav-mod-seminars",  perm: "school:seminars" },
];

const schoolItems = [
  { to: "/scuola",             label: "Dashboard scuola",  icon: LayoutDashboard, testId: "nav-school-dashboard",     perm: "school:dashboard" },
  { to: "/scuola/piano",       label: "Calendario didattico", icon: Calendar,     testId: "nav-school-piano",         perm: "school:piano" },
  { to: "/scuola/studenti",    label: "Studenti",           icon: GraduationCap,   testId: "nav-school-students",      perm: "school:students" },
  { to: "/scuola/statistiche", label: "Statistiche studenti", icon: LineChart,     testId: "nav-school-stats",         perm: "school:statistics" },
  { to: "/scuola/docenti",     label: "Docenti",            icon: UserSquare,      testId: "nav-school-teachers",      perm: "school:teachers" },
  { to: "/scuola/corsi",       label: "Corsi",              icon: BookOpen,        testId: "nav-school-courses",       perm: "school:courses" },
  { to: "/scuola/seminari",    label: "Seminari",           icon: Presentation,    testId: "nav-school-seminars",      perm: "school:seminars" },
  { to: "/scuola/attestati",   label: "Attestati",          icon: Award,           testId: "nav-school-certgen",       perm: "school:certs" },
  { to: "/scuola/iscrizioni-seminari", label: "Iscrizioni seminari", icon: Ticket,  testId: "nav-school-seminar-bookings", perm: "school:seminar_bookings" },
  { to: "/scuola/presenze",    label: "Presenze",           icon: ClipboardCheck,  testId: "nav-school-attendance",    perm: "school:attendance" },
  { to: "/scuola/permessi",    label: "Permessi",           icon: Clock,           testId: "nav-school-permessi",      perm: "school:permessi" },
  { to: "/scuola/esami",       label: "Esami",              icon: Award,           testId: "nav-school-exams",         perm: "school:exams" },
  { to: "/scuola/storico-esami", label: "Storico Esami",    icon: History,         testId: "nav-school-storico-esami", perm: "school:storico_esami" },
  { to: "/scuola/crediti-pratici", label: "Crediti pratici", icon: Sparkles,       testId: "nav-school-practical",     perm: "school:practical" },
];

const mansioniItems = [
  { to: "/mansioni",         label: "Catalogo Mansioni", icon: ClipboardList, testId: "nav-mansioni-catalog", end: true, perm: "mansioni:catalog" },
  { to: "/mansioni/turni",   label: "Turni",             icon: CalendarClock, testId: "nav-mansioni-turni",             perm: "mansioni:turni" },
  { to: "/mansioni/impegni", label: "Impegni esterni",   icon: Compass,       testId: "nav-mansioni-impegni",           perm: "mansioni:impegni" },
  { to: "/mansioni/planner", label: "Planner unificato", icon: LayoutGrid,    testId: "nav-mansioni-planner",           perm: "mansioni:planner" },
  { to: "/mansioni/liberi",  label: "Studenti liberi",   icon: UserCheck,     testId: "nav-mansioni-liberi",            perm: "mansioni:planner" },
  { to: "/mansioni/statistiche", label: "Statistiche turni", icon: PieChart, testId: "nav-mansioni-stats",            perm: "mansioni:stats" },
  { to: "/mansioni/statistiche-impegni", label: "Statistiche impegni", icon: PieChart, testId: "nav-mansioni-impegni-stats", perm: "mansioni:stats" },
];

const administrationItems = [
  { to: "/amministrazione",             label: "Panoramica",  icon: LayoutDashboard, testId: "nav-admin-dashboard", end: true, perm: "administration:dashboard" },
  { to: "/amministrazione/movimenti",   label: "Movimenti",   icon: Receipt,         testId: "nav-admin-movements",           perm: "administration:movements" },
  { to: "/amministrazione/casse",       label: "Casse & Conti", icon: Wallet,        testId: "nav-admin-accounts",            perm: "administration:accounts" },
  { to: "/amministrazione/categorie",   label: "Categorie",   icon: LayoutGrid,      testId: "nav-admin-categories",          perm: "administration:categories" },
  { to: "/amministrazione/report",      label: "Report",      icon: PieChart,        testId: "nav-admin-report",              perm: "administration:reports" },
];

const walletItems = [
  { to: "/wallet",           label: "Wallet studenti/docenti", icon: Wallet,   testId: "nav-wallet-overview", end: true, perm: "wallet:overview" },
  { to: "/wallet/ricariche", label: "Richieste ricarica",      icon: Banknote, testId: "nav-wallet-topups",              perm: "wallet:topups" },
  { to: "/wallet/bar",       label: "Incassi bar",             icon: Receipt,  testId: "nav-wallet-bar",                 perm: "wallet:bar" },
];

const teacherItems = [
  { to: "/docente",            label: "Dashboard",         icon: LayoutDashboard, testId: "nav-teacher-dashboard", end: true },
  { to: "/docente/wallet",     label: "Il mio wallet",     icon: Wallet,          testId: "nav-teacher-wallet" },
  { to: "/docente/presenze",   label: "Presenze",          icon: ClipboardCheck,  testId: "nav-teacher-attendance" },
  { to: "/docente/documenti",  label: "Documenti",         icon: FileText,        testId: "nav-teacher-documents" },
  { to: "/scuola/piano",       label: "Calendario",        icon: Calendar,        testId: "nav-teacher-piano" },
  { to: "/scuola/corsi",       label: "Catalogo corsi",    icon: BookOpen,        testId: "nav-teacher-courses" },
  { to: "/scuola/statistiche", label: "Statistiche studenti", icon: LineChart,    testId: "nav-teacher-stats" },
  { to: "/scuola/esami",       label: "Appelli",           icon: Award,           testId: "nav-teacher-exams" },
];

const staffCommItems = [
  { to: "/scuola/avvisi", label: "Bacheca avvisi", icon: BellRing,      testId: "nav-avvisi",    perm: "segreteria:avvisi" },
  { to: "/scuola/documenti-condivisi", label: "Documenti condivisi", icon: FileText, testId: "nav-shared-docs", perm: "segreteria:documenti" },
  { to: "/richieste",     label: "Richieste",      icon: Inbox,         testId: "nav-requests",  perm: "segreteria:requests" },
  { to: "/messaggi",      label: "Messaggi",       icon: MessageSquare, testId: "nav-messages",  perm: "segreteria:messages" },
];

const studentGroups = [
  { title: "In evidenza", items: [
    { to: "/portale/bacheca",    label: "Bacheca",         icon: BellRing,       testId: "nav-portal-bacheca" },
    { to: "/portale/piano",      label: "Calendario",      icon: Calendar,       testId: "nav-portal-piano" },
    { to: "/portale/profilo",    label: "Il mio profilo",  icon: UserSquare,     testId: "nav-portal-profile" },
    { to: "/portale/pagamenti",  label: "Posizione contabile", icon: Receipt,   testId: "nav-portal-payments", modularOnly: true },
  ]},
  { title: "Didattica", items: [
    { to: "/portale/corsi",      label: "I miei corsi",    icon: BookOpen,       testId: "nav-portal-courses" },
    { to: "/portale/materiali",  label: "Materiali",       icon: FolderOpen,     testId: "nav-portal-materials" },
    { to: "/portale/esercizi",   label: "Esercizi",        icon: NotebookPen,    testId: "nav-portal-exercises" },
    { to: "/portale/appelli",    label: "Appelli",         icon: Award,          testId: "nav-portal-exams" },
    { to: "/portale/libretto",   label: "Libretto",        icon: GraduationCap,  testId: "nav-portal-booklet" },
    { to: "/portale/frequenza",  label: "Frequenza",       icon: ClipboardCheck, testId: "nav-portal-attendance" },
  ]},
  { title: "Vita comunitaria", items: [
    { to: "/portale/turni",      label: "Turni & impegni", icon: ClipboardList,  testId: "nav-portal-turni" },
    { to: "/portale/permessi",   label: "Permessi",        icon: Clock,          testId: "nav-portal-permessi" },
  ]},
  { title: "Documenti", items: [
    { to: "/portale/documenti",  label: "Documenti",       icon: FileText,       testId: "nav-portal-documents" },
    { to: "/portale/attestati",  label: "Attestati",       icon: Sparkles,       testId: "nav-portal-certificates" },
  ]},
  { title: "Servizi", items: [
    { to: "/portale/wallet",     label: "Wallet",          icon: Wallet,         testId: "nav-portal-wallet" },
  ]},
  { title: "Comunicazioni", items: [
    { to: "/portale/messaggi",   label: "Messaggi",        icon: MessageSquare,  testId: "nav-portal-messages" },
    { to: "/portale/tutor",      label: "Il mio tutor",    icon: UserCheck,      testId: "nav-portal-tutor", modularOnly: true },
    { to: "/portale/richieste",  label: "Richieste",       icon: Inbox,          testId: "nav-portal-requests" },
    { to: "/helpdesk",           label: "Help Desk",       icon: LifeBuoy,       testId: "nav-portal-helpdesk" },
  ]},
];

function GroupHeader({ icon: Icon, title, open, onToggle, testId, badge }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-center justify-between gap-2 px-5 py-2.5 mt-3 text-[11px] uppercase tracking-[0.18em] text-stone-400 hover:text-white transition-colors duration-150 font-medium"
    >
      <span className="flex items-center gap-2.5">
        <Icon size={14} strokeWidth={2} /> {title}
        {badge != null && badge !== 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E2725B] text-white normal-case tracking-normal font-semibold">{badge}</span>
        )}
      </span>
      <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`} />
    </button>
  );
}

function NavItem({ to, label, icon: Icon, testId, end, badge, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={() => onNavigate && onNavigate()}
      className={({ isActive }) =>
        `group flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-[15px] transition-colors duration-150 ${
          isActive
            ? "bg-white/12 text-white sidebar-active font-semibold"
            : "text-stone-300 hover:bg-white/6 hover:text-white"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E2725B] text-white font-semibold">{badge}</span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobile = false, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const el = navRef.current?.querySelector("a.active, a[aria-current='page']");
      if (el) el.scrollIntoView({ block: "nearest" });
    }, 150);
    return () => clearTimeout(t);
  }, [location.pathname]);
  const staff = isStaff(user);
  const student = isStudent(user);
  const { data: barStatus } = useQuery({
    queryKey: ["bar-status"], enabled: !!student, staleTime: 300000,
    queryFn: async () => (await api.get("/bar/status")).data,
  });
  const teacher = isTeacher(user);
  const adminSegreteria = isAdminOrSegreteria(user);
  const fundraising = isFundraising(user);

  const roleLabel = student
    ? "Studente"
    : user?.role === "admin" ? "Direzione"
    : user?.role === "direzione" ? "Direzione"
    : user?.role === "docente" ? "Docente"
    : user?.role === "raccolta_fondi" ? "Raccolta fondi"
    : user?.role === "economato" ? "Economato"
    : "Segreteria";

  const [openFundraising, setOpenFundraising] = useState(false);
  const [openSchool, setOpenSchool] = useState(false);
  const [openModular, setOpenModular] = useState(false);
  const [openMansioni, setOpenMansioni] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openTeacher, setOpenTeacher] = useState(false);
  const [openComm, setOpenComm] = useState(false);
  const [openWallet, setOpenWallet] = useState(false);
  const economato = isEconomato(user);

  // Auto-apri il gruppo sidebar in base alla route corrente (funziona sia
  // per navigazione manuale sia dal Dashboard hub). Non chiude gli altri
  // gruppi già aperti dall'utente.
  useEffect(() => {
    const p = location.pathname;
    if (teacher && teacherItems.some((it) => it.to !== "/docente" && p.startsWith(it.to))) setOpenTeacher(true);
    else if (p.startsWith("/scuola")) setOpenSchool(true);
    else if (p.startsWith("/modulare")) setOpenModular(true);
    else if (p.startsWith("/amministrazione")) setOpenAdmin(true);
    else if (p.startsWith("/mansioni")) setOpenMansioni(true);
    else if (p.startsWith("/wallet")) setOpenWallet(true);
    else if (
      p.startsWith("/donatori") || p.startsWith("/donazioni") ||
      p.startsWith("/scadenze") || p.startsWith("/followup") || p.startsWith("/donatori-ritardo") ||
      p.startsWith("/comunicazioni") || p.startsWith("/report")
    ) setOpenFundraising(true);
    else if (p.startsWith("/richieste") || p.startsWith("/messaggi")) setOpenComm(true);
    else if (p.startsWith("/docente")) setOpenTeacher(true);
  }, [location.pathname]);

  const collapseAll = () => {
    setOpenFundraising(false);
    setOpenSchool(false);
    setOpenModular(false);
    setOpenMansioni(false);
    setOpenAdmin(false);
    setOpenTeacher(false);
    setOpenComm(false);
    setOpenWallet(false);
  };

  const { data: reqStats } = useQuery({
    queryKey: ["requests-stats-badge"],
    queryFn: async () => (await api.get("/requests-stats/overview")).data,
    enabled: staff,
    refetchInterval: 30_000,
  });
  const { data: threadStats } = useQuery({
    queryKey: ["threads-stats-badge"],
    queryFn: async () => (await api.get("/threads-stats/overview")).data,
    enabled: student,
    refetchInterval: 30_000,
  });
  const { data: docsUnseen } = useQuery({
    queryKey: ["shared-docs-unseen"],
    queryFn: async () => (await api.get("/shared-documents/me/unseen-count")).data,
    enabled: student || teacher,
    refetchInterval: 60_000,
  });
  const ticketCount = useTicketCount(adminSegreteria);

  return (
    <aside className={
      mobile
        ? "flex flex-col h-full bg-[#1C352D] text-stone-100 w-full"
        : "hidden lg:flex w-72 fixed inset-y-0 left-0 z-40 bg-[#1C352D] text-stone-100 flex-col shadow-[4px_0_24px_-12px_rgba(0,0,0,0.35)]"
    }>
      <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3.5 shrink-0">
        <div className="w-11 h-11 rounded-xl bg-[#E2725B] grid place-items-center text-white shrink-0">
          <Sprout size={22} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold tracking-tight leading-tight text-[17px]">RDM ClassRoom</div>
          <div className="text-[10px] text-stone-400 uppercase tracking-[0.22em] mt-0.5">
            {student ? "Portale Studente" : teacher ? "Portale Docente" : economato ? "Economato" : "Segreteria & Scuola"}
          </div>
        </div>
      </div>

      <nav ref={navRef} className="flex-1 min-h-0 py-3 px-2 space-y-0.5 overflow-y-auto overscroll-contain sidebar-scroll" data-testid="sidebar-nav">
        {student && (
          <div className="pt-1">
            {studentGroups.filter((g) => !(g.title === "Vita comunitaria" && user?.track_type === "modulare")).map((g) => (
              <div key={g.title} className="pt-3" data-testid={`nav-portal-section-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="px-5 pb-1 text-[10px] uppercase tracking-[0.2em] text-stone-400 font-medium">{g.title}</div>
                <div className="space-y-0.5">
                  {g.items.filter((it) => !it.modularOnly || user?.track_type === "modulare").map((it) => {
                    const badge = it.to === "/portale/messaggi" ? threadStats?.unread : it.to === "/portale/documenti" ? docsUnseen?.unseen : undefined;
                    return <NavItem key={it.to} {...it} badge={badge} onNavigate={onNavigate} />;
                  })}
                  {g.title === "Servizi" && barStatus?.cashier && (
                    <NavItem to="/portale/bar" label="Cassa bar" icon={ShoppingCart} testId="nav-portal-bar" onNavigate={onNavigate} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {economato && (
          <div className="space-y-0.5 pt-3">
            {walletItems.map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
          </div>
        )}

        {fundraising && (
          <div className="space-y-0.5 pt-3">
            {fundraisingItems.map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
          </div>
        )}

        {teacher && !adminSegreteria && (
          <>
            <GroupHeader
              icon={UserSquare}
              title="Portale docente"
              open={openTeacher}
              onToggle={() => setOpenTeacher((v) => !v)}
              testId="nav-group-teacher"
            />
            {openTeacher && (
              <div className="space-y-0.5">
                {teacherItems.map((it) => <NavItem key={it.to} {...it} badge={it.to === "/docente/documenti" ? docsUnseen?.unseen : undefined} onNavigate={onNavigate} />)}
              </div>
            )}
            <GroupHeader
              icon={Send}
              title="Comunicazioni"
              open={openComm}
              onToggle={() => setOpenComm((v) => !v)}
              testId="nav-group-teacher-comm"
            />
            {openComm && (
              <div className="space-y-0.5">
                {staffCommItems.map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
              </div>
            )}
            <div className="pt-3 mt-2 border-t border-white/10">
              <NavItem
                to="/helpdesk"
                label="Help Desk"
                icon={LifeBuoy}
                testId="nav-teacher-helpdesk"
                onNavigate={onNavigate}
              />
            </div>
          </>
        )}

        {adminSegreteria && (
          <>
            <div className="px-4 pt-2 flex justify-end">
              <button
                onClick={collapseAll}
                data-testid="nav-collapse-all"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-stone-400 hover:text-white transition-colors px-2.5 py-1 rounded-md hover:bg-white/5"
                title="Chiudi tutti i menu"
              >
                <ChevronsDownUp size={12}/> Chiudi tutti
              </button>
            </div>

            {canAccessModule(user, "school") && (
              <>
                <GroupHeader
                  icon={School}
                  title="Scuola Residenziale"
                  open={openSchool}
                  onToggle={() => setOpenSchool((v) => !v)}
                  testId="nav-group-school"
                />
                {openSchool && (
                  <div className="space-y-0.5">
                    {schoolItems.filter((it) => canAccess(user, "school", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
                <GroupHeader
                  icon={GraduationCap}
                  title="Scuola Modulare"
                  open={openModular}
                  onToggle={() => setOpenModular((v) => !v)}
                  testId="nav-group-modular"
                />
                {openModular && (
                  <div className="space-y-0.5">
                    {modularItems.filter((it) => canAccess(user, "school", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
              </>
            )}

            {canAccessModule(user, "administration") && (
              <>
                <GroupHeader
                  icon={Banknote}
                  title="Amministrazione"
                  open={openAdmin}
                  onToggle={() => setOpenAdmin((v) => !v)}
                  testId="nav-group-admin"
                />
                {openAdmin && (
                  <div className="space-y-0.5">
                    {administrationItems.filter((it) => canAccess(user, "administration", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
              </>
            )}

            {canAccessModule(user, "mansioni") && (
              <>
                <GroupHeader
                  icon={ClipboardList}
                  title="Mansioni"
                  open={openMansioni}
                  onToggle={() => setOpenMansioni((v) => !v)}
                  testId="nav-group-mansioni"
                />
                {openMansioni && (
                  <div className="space-y-0.5">
                    {mansioniItems.filter((it) => canAccess(user, "mansioni", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
              </>
            )}

            {canAccessModule(user, "wallet") && (
              <>
                <GroupHeader
                  icon={Wallet}
                  title="Wallet"
                  open={openWallet}
                  onToggle={() => setOpenWallet((v) => !v)}
                  testId="nav-group-wallet"
                />
                {openWallet && (
                  <div className="space-y-0.5">
                    {walletItems.filter((it) => canAccess(user, "wallet", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
              </>
            )}

            {canAccessModule(user, "vigilanza") && (
              <div className="pt-2">
                <NavItem to="/vigilanza" label="Turni Vigilanza" icon={ShieldCheck} testId="nav-vigilanza" onNavigate={onNavigate} />
              </div>
            )}

            {canAccessModule(user, "fundraising") && (
              <>
                <GroupHeader
                  icon={HeartHandshake}
                  title="Raccolta fondi"
                  open={openFundraising}
                  onToggle={() => setOpenFundraising((v) => !v)}
                  testId="nav-group-fundraising"
                />
                {openFundraising && (
                  <div className="space-y-0.5">
                    {fundraisingItems.filter((it) => !it.perm || canAccess(user, "fundraising", it.perm.split(":")[1])).map((it) => <NavItem key={it.to} {...it} onNavigate={onNavigate} />)}
                  </div>
                )}
              </>
            )}

            {canAccessModule(user, "segreteria") && (
              <>
                <GroupHeader
                  icon={Send}
                  title="Segreteria"
                  open={openComm}
                  onToggle={() => setOpenComm((v) => !v)}
                  testId="nav-group-comm"
                  badge={reqStats?.pending > 0 ? reqStats.pending : undefined}
                />
                {openComm && (
                  <div className="space-y-0.5">
                    {staffCommItems.filter((it) => canAccess(user, "segreteria", it.perm.split(":")[1])).map((it) => {
                      const badge = it.to === "/richieste" ? reqStats?.pending : undefined;
                      return <NavItem key={it.to} {...it} badge={badge} onNavigate={onNavigate} />;
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {adminSegreteria && (
          <div className="pt-3 mt-2 border-t border-white/10">
            <NavItem
              to="/helpdesk"
              label="Help Desk"
              icon={LifeBuoy}
              testId="nav-helpdesk"
              badge={ticketCount}
              onNavigate={onNavigate}
            />
          </div>
        )}

        {adminSegreteria && canAccessModule(user, "settings") && (
          <div className="pt-1">
            <NavItem
              to="/impostazioni"
              label="Impostazioni"
              icon={Settings}
              testId={NAV.settings}
              onNavigate={onNavigate}
            />
          </div>
        )}
      </nav>

      <div className="border-t border-white/10 p-4 space-y-3 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/10 grid place-items-center text-sm font-semibold">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name || "—"}</div>
            <div className="text-[11px] text-stone-400 truncate">{roleLabel} · {user?.email}</div>
          </div>
        </div>
        <div className="flex justify-center items-center gap-2 flex-wrap">
          <PushEnableButton />
          <LanguageSwitch loggedIn dark withEs={user?.role === "studente"} />
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="w-full bg-white/10 hover:bg-white/20 text-white border-0 transition-colors duration-150"
          data-testid={AUTH.logoutBtn}
          onClick={() => { logout(); navigate("/login"); }}
        >
          <LogOut size={14} className="mr-2" /> Esci
        </Button>
      </div>
    </aside>
  );
}
